import { toast } from "@/components/ui/";
import { QueryKeys } from "@/constants/Query";
import type { PatientTabsResponse } from "@/features/PatientTabs/network/usePatientTabs";
import { captureError } from "@/lib/monitoring";
import { socketsAtom } from "@/store/socketsAtom";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useState } from "react";
import {
  AsyncMessageTypes,
  ChatFilterMode,
  type ChatMessage,
  MessageType,
} from "../types/chatMessageTypes";
import {
  type ChatQueryData,
  addToChatMessageCache,
  updateChatMessageCache,
} from "../utils/chatCacheUtils";

interface SocketIOChatMessage {
  type: MessageType | AsyncMessageTypes;
  roomToken: string;
  user?: {
    userId: number;
    name: string;
  };
  created?: string;
  uuid?: string;
  text?: string;
  tag?: string;
  clientUuid?: string;
  message?: ChatMessage;
  questionnaireName?: string;
}

// This is a simplified version of the full message type,
// but sufficient for our use case of updating customerWaitingSince
interface RoomMetadataMessage {
  openEncounterId: string;
  encounterState: {
    customerWaitingSince: string;
  };
  roomToken: string;
  customer: {
    userId: number;
    patients: {
      patientId: string;
    }[];
  };
}

/**
 * Handles global socket events for all chat rooms.
 * This hook listens to socket messages for all rooms and updates their caches accordingly.
 * Should be mounted once at the app root level (in __root.tsx) to ensure all chat data
 * stays in sync regardless of which room the user is viewing.
 *
 * Handles: chat messages, async messages, room metadata updates, and refresh triggers.
 */
export function useChatSocketGlobalHandler() {
  const queryClient = useQueryClient();
  const socket = useAtomValue(socketsAtom)?.chat;
  const handleTriggerRefresh = useCallback(() => {
    socket?.emit("refresh", null);
  }, [socket]);

  const handleUpdateRoomMetadata = useCallback(
    (message: RoomMetadataMessage) => {
      queryClient.setQueryData<PatientTabsResponse>(
        QueryKeys.Provider.PATIENT_TABS,
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            patientTabs: oldData.patientTabs.map((tab) => {
              // If we find a tab match, update patientWaitingSince
              if (
                tab.patient.id === message.customer?.patients?.[0]?.patientId
              ) {
                return {
                  ...tab,
                  sla: {
                    patientWaitingSince:
                      // This is null for when an encounter is closed, so we can optional chain it
                      message?.encounterState?.customerWaitingSince,
                  },
                };
              }
              // Otherwise, return the tab unchanged
              return tab;
            }),
          };
        },
      );
    },
    [queryClient],
  );

  /**
   * Updates chat message caches for the specified filter modes
   * @param roomToken The room token to update
   * @param message The message to add to the cache
   * @param filterModes The filter modes to update (LIVE, ASYNC, and/or COMBINED)
   */
  const updateCachesForFilterModes = useCallback(
    (
      roomToken: string,
      message: ChatMessage,
      filterModes: ChatFilterMode[],
    ) => {
      for (const mode of filterModes) {
        queryClient.setQueryData<ChatQueryData>(
          QueryKeys.Patient.Chat.Messages.DEFAULT(roomToken, mode),
          (oldData) => addToChatMessageCache(oldData, message),
        );
      }
    },
    [queryClient],
  );

  const handleLiveMessage = useCallback(
    (message: SocketIOChatMessage | ChatMessage) => {
      // Ignore typing and read status messages - these are handled per-room
      if (
        message.type === MessageType.START_TYPING ||
        message.type === MessageType.STOP_TYPING ||
        message.type === MessageType.READ
      ) {
        return;
      }

      // Update the cache for the message's room
      const roomToken = message.roomToken;
      if (!roomToken) return;

      switch (message.type) {
        case MessageType.ACK:
          // Update LIVE and COMBINED caches
          updateCachesForFilterModes(
            roomToken,
            message.message as ChatMessage,
            [ChatFilterMode.LIVE, ChatFilterMode.COMBINED],
          );
          break;
        case MessageType.TEXT:
        case MessageType.PHOTO:
        case MessageType.VIDEO_CALL:
        case MessageType.VIDEO_CALL_CANCELED:
        case MessageType.HANDOFF:
        case MessageType.NOTE_STARTED:
        case MessageType.NOTE_CLOSED:
        case MessageType.STEAL:
        case MessageType.ESCALATE_TO_MD:
        case MessageType.PROVIDER_BIO:
        case MessageType.PATIENT_INFO:
        case MessageType.PATIENT_INFO_RESPONSE:
        case MessageType.CONFIRM_RX:
        case MessageType.CONFIRM_RX_RESPONSE:
        case MessageType.DOCTOR_HAS_ENTERED_TO_CHAT:
        case MessageType.ENCOUNTER_START:
        case MessageType.ENCOUNTER_END:
        case MessageType.ENCOUNTER_REOPEN:
        case MessageType.ENCOUNTER_CLOSE:
        case MessageType.INCOMPLETE_ON_CLOSE:
        case MessageType.QUESTIONNAIRE:
        case MessageType.CONFIRM_LABS:
        case MessageType.CARE_PLAN:
          // Update LIVE and COMBINED caches
          updateCachesForFilterModes(roomToken, message as ChatMessage, [
            ChatFilterMode.LIVE,
            ChatFilterMode.COMBINED,
          ]);
          break;
        case MessageType.EXTERNAL_TRANSMISSION_FAILED:
          toast.error(
            "Failed to send message. Please try again and submit a bug report if the problem persists.",
          );
          break;
        case MessageType.PIN:
        case MessageType.UNACK:
        case MessageType.REFRESH_SUBSCRIPTIONS:
        case MessageType.ARTICLE:
        case MessageType.SINGLE_SELECT:
        case MessageType.MULTI_SELECT:
        case MessageType.CHIEF_COMPLAINT:
        case MessageType.CHIEF_COMPLAINT_RESPONSE:
        case MessageType.CONFIRM_CHIEF_COMPLAINT:
        case MessageType.CONFIRM_CHIEF_COMPLAINT_RESPONSE:
        case MessageType.NEXT_QUESTION:
        case MessageType.NEXT_QUESTION_RESPONSE:
        case MessageType.EXIT_NEXT_QUESTION:
        case MessageType.END_CHAT:
        case MessageType.CONFIRM_END_CHAT:
        case MessageType.SUGGESTED_RESPONSE:
        case MessageType.INITIATE_RATING:
        case MessageType.CONFIRM_RATING:
        case MessageType.SYMPTOM_EXTRACTOR:
        case MessageType.INTENT:
        case MessageType.INTENT_RESPONSE:
        case MessageType.CONTENT_SHARE:
        case MessageType.WAIT_INTAKE_REVIEW:
        case MessageType.CONTENT_WHILE_YOU_WAIT:
        case MessageType.AUTOSEND_INTRO:
        case MessageType.PMH_QUESTION:
        case MessageType.PMH_RESPONSE:
        case MessageType.ROUTING:
        case MessageType.CONFIRM_LABS_RESPONSE:
          break;
        default:
          captureError(
            new Error(
              `Unknown message type in socket message handler: ${message.type}`,
            ),
          );
          break;
      }
    },
    [updateCachesForFilterModes],
  );

  const handleAsyncMessage = useCallback(
    (message: SocketIOChatMessage) => {
      const roomToken = message.roomToken;
      if (!roomToken) return;

      switch (message.type) {
        case AsyncMessageTypes.TEXT:
        case AsyncMessageTypes.QUESTIONNAIRE:
        case AsyncMessageTypes.CARE_PLAN:
          // Update ASYNC and COMBINED caches
          updateCachesForFilterModes(roomToken, message as ChatMessage, [
            ChatFilterMode.ASYNC,
            ChatFilterMode.COMBINED,
          ]);
          break;
        case AsyncMessageTypes.ACK:
          // Update ASYNC and COMBINED caches
          updateCachesForFilterModes(
            roomToken,
            message.message as ChatMessage,
            [ChatFilterMode.ASYNC, ChatFilterMode.COMBINED],
          );
          break;
        case AsyncMessageTypes.READ:
          // We don't need to do anything with read receipts just yet (or ever maybe?)
          break;
        default:
          captureError(
            new Error(
              `Unknown async message type in socket message handler: ${message.type}`,
            ),
          );
          break;
      }
    },
    [updateCachesForFilterModes],
  );

  useEffect(() => {
    if (!socket) return;

    socket.on("trigger_refresh", handleTriggerRefresh);
    socket.on("update_room_metadata", handleUpdateRoomMetadata);
    socket.on("chat_message", handleLiveMessage);
    socket.on("async_message", handleAsyncMessage);

    return () => {
      socket.off("trigger_refresh", handleTriggerRefresh);
      socket.off("update_room_metadata", handleUpdateRoomMetadata);
      socket.off("chat_message", handleLiveMessage);
      socket.off("async_message", handleAsyncMessage);
    };
  }, [
    socket,
    handleTriggerRefresh,
    handleUpdateRoomMetadata,
    handleLiveMessage,
    handleAsyncMessage,
  ]);
}

/**
 * Handles room-specific socket events for a single chat room.
 * This hook manages typing indicators and message updates for the specified room only.
 * Use this hook in components that display a specific chat room.
 */
export function useChatSocketRoom(roomToken: string) {
  const socket = useAtomValue(socketsAtom)?.chat;
  const [typingUser, setTypingUser] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { patientId } = useParams({ from: "/patient/$patientId" });

  // Handle typing indicators for the current room only
  const handleTypingIndicators = useCallback(
    (message: SocketIOChatMessage | ChatMessage) => {
      if (message.roomToken !== roomToken) return;

      switch (message.type) {
        case MessageType.START_TYPING:
          setTypingUser(true);
          break;
        case MessageType.STOP_TYPING:
          setTypingUser(false);
          break;
      }
    },
    [roomToken],
  );

  // Handle message updates (questionnaire completion toasts, etc.)
  const handleUpdateChatMessage = useCallback(
    (message: { updatedMessage: SocketIOChatMessage; roomToken: string }) => {
      if (message.roomToken !== roomToken) return;

      // If the message is a questionnaire, invalidate the mental health questionnaire query
      // so that the provider can see the latest questionnaire.
      if (message.updatedMessage.type === MessageType.QUESTIONNAIRE) {
        toast.success(
          `${message.updatedMessage.questionnaireName} has been completed.`,
          {
            duration: Number.POSITIVE_INFINITY,
          },
        );
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.MentalHealthQuestionnaire.DEFAULT(
              patientId,
            ),
        });
      }

      // Update all three filter mode caches when a message is updated
      // since we don't know which cache(s) contain the message
      for (const mode of [
        ChatFilterMode.LIVE,
        ChatFilterMode.ASYNC,
        ChatFilterMode.COMBINED,
      ]) {
        queryClient.setQueryData<ChatQueryData>(
          QueryKeys.Patient.Chat.Messages.DEFAULT(roomToken, mode),
          (oldData) =>
            updateChatMessageCache(
              oldData,
              message.updatedMessage as ChatMessage,
            ),
        );
      }
    },
    [queryClient, patientId, roomToken],
  );

  useEffect(() => {
    if (!socket || !roomToken) return;

    setTypingUser(false);

    socket.on("chat_message", handleTypingIndicators);
    socket.on("update_chat_message", handleUpdateChatMessage);

    return () => {
      socket.off("chat_message", handleTypingIndicators);
      socket.off("update_chat_message", handleUpdateChatMessage);

      setTypingUser(false);
    };
  }, [socket, handleTypingIndicators, handleUpdateChatMessage, roomToken]);

  return {
    socket,
    typingUser,
  };
}
